"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.sanitizeRedirectUrl = exports.getSafeHttpUrl = exports.isValidURL = exports.stripScriptTags = exports.escapeHtml = exports.encodeUrl = void 0;
const constants_1 = require("./constants");
/**
 * Escapes HTML-significant characters so a string can be safely interpolated
 * into `dangerouslySetInnerHTML` without risk of injecting markup/XSS.
 * Pure string logic (no DOM dependency) so it behaves identically during SSR
 * and in the browser.
 *
 * JSX Example Usage
 * <div dangerouslySetInnerHTML={{
 *   __html: escapeHtml('<svg><g/onload=alert(2)//<p>'),
 * }} />
 */
const escapeHtml = (value) => {
    return String(value)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
};
exports.escapeHtml = escapeHtml;
/**
 * Helper to mainly handle XSS Attacks `?q=<script>` for `dangerouslySetInnerHTML`
 *
 * https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/encodeURI
 * Problem is a `encodedURI`, can be run on itself creating varying results
 * a = encodeURI('<cats>') => "%3Ccats%3E"
 * b = encodeURI(a) => "%253Ccats%253E"
 */
const encodeUrl = (value) => {
    return String(value)
        .replace(/</g, '%3C')
        .replace(/>/g, '%3E')
        .replace(/"/g, '%22');
};
exports.encodeUrl = encodeUrl;
const stripScriptTags = (value) => {
    return String(value).replace(/<script[\s\S]*?>[\s\S]*?<\/script>/gi, '');
};
exports.stripScriptTags = stripScriptTags;
const isValidURL = (urlString) => {
    var _a;
    try {
        return !!((_a = new URL(urlString)) === null || _a === void 0 ? void 0 : _a.href);
    }
    catch (error) {
        return false;
    }
};
exports.isValidURL = isValidURL;
/**
 * Returns an encoded, safe URL only when the input is a well-formed http(s)
 * URL, otherwise an empty string. Use for tainted values that flow into link
 * `href` attributes/DOM sinks (e.g. `<link rel="canonical">`).
 *
 * Guards the same payload classes as `sanitizeRedirectUrl`:
 *   - `javascript:` / `data:` (and any non-web scheme) — rejected by the
 *     `new URL` protocol check, which is stricter than a regex.
 *   - inline event handlers (`onerror=`, `onload=`, ...) and `<script>` tags —
 *     rejected explicitly below.
 *
 * The accepted result is passed through `encodeUrl`, which encodes the markup
 * chars `<`, `>`, `"` (so the value can't break out of the attribute) without
 * touching `%`, `/` or query syntax — avoiding double-encoding and preserving
 * the URL. This explicit re-encoding also breaks the DOM-XSS taint flow at the
 * sink for static analysis.
 */
const getSafeHttpUrl = (urlString) => {
    if (!urlString) {
        return '';
    }
    try {
        const { protocol } = new URL(urlString);
        if (protocol !== 'http:' && protocol !== 'https:') {
            return '';
        }
    }
    catch (error) {
        return '';
    }
    const isOnCallbackAttack = /\bon\w+=/i.test(urlString);
    const isScriptAttack = /<\s*script/i.test(urlString);
    if (isOnCallbackAttack || isScriptAttack) {
        return '';
    }
    return encodeUrl(urlString);
};
exports.getSafeHttpUrl = getSafeHttpUrl;
const sanitizeRedirectUrl = (redirectUrl) => {
    const { SITE_URL } = process.env;
    const homepage = typeof SITE_URL === 'string' && isValidURL(SITE_URL)
        ? new URL(SITE_URL).href
        : constants_1.PRODUCTION_BASE_URL;
    // accepts only http or https
    const isJavascriptAttack = /javascript:/i.test(redirectUrl);
    const isDataAttack = /data:/i.test(redirectUrl);
    const isOnCallbackAttack = /\bon\w+=/g.test(redirectUrl);
    const isScriptAttack = /<\s*script\s*>/gi.test(redirectUrl);
    if (isJavascriptAttack || isDataAttack || isOnCallbackAttack || isScriptAttack) {
        return homepage;
    }
    // Encode parameters
    const sanitizedRedirectUrl = encodeURI(redirectUrl);
    return sanitizedRedirectUrl;
};
exports.sanitizeRedirectUrl = sanitizeRedirectUrl;
